import { Dropdown, Icon, IDropdownOption, SearchBox } from "@fluentui/react";
import React, { useContext, useMemo, useState } from "react";
import styled from "styled-components";
import { ColumnOptionsContext } from "./ColumnOptions";
import { SelectAllCheckbox } from "./SelectAllCheckbox";
import { Column, Grouping } from "./types";

const ALPHA_FALLBACK: Grouping = {
  key: "alphabetical",
  label: "Alphabetical",
  groupOf: () => null,
};

type Bucket = {
  key: string;
  label: string;
  order: number;
  items: Column[];
};

export const AvailableFieldsPane = () => {
  const { columns, groupings, onToggleColumnVisibility, t, id } =
    useContext(ColumnOptionsContext);

  const groupingList =
    groupings && groupings.length ? groupings : [ALPHA_FALLBACK];

  const [groupByKey, setGroupByKey] = useState(groupingList[0].key);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const grouping =
    groupingList.find((g) => g.key === groupByKey) || groupingList[0];

  const available = useMemo(
    () => columns.filter((c) => c.isPresentInColumnOptions !== false),
    [columns]
  );

  const term = search.trim().toLowerCase();

  const buckets = useMemo<Bucket[]>(() => {
    const filtered = term
      ? available.filter((c) => (c.label || "").toLowerCase().includes(term))
      : available;

    const byKey: Record<string, Bucket> = {};
    for (const c of filtered) {
      const g = grouping.groupOf(c);
      const key = g ? g.key : "__all__";
      if (!byKey[key]) {
        byKey[key] = {
          key,
          label: g ? g.label : "",
          order: g?.order ?? 50,
          items: [],
        };
      }
      byKey[key].items.push(c);
    }
    const arr = Object.values(byKey);
    arr.forEach((b) =>
      b.items.sort((a, c) => (a.label || "").localeCompare(c.label || ""))
    );
    arr.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
    return arr;
  }, [available, grouping, term]);

  const isFlat = buckets.length === 1 && buckets[0].key === "__all__";

  const groupByOptions: IDropdownOption[] = groupingList.map((g) => ({
    key: g.key,
    text: g.label,
  }));

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const isExpanded = (key: string) => (term ? true : !!expanded[key]);

  const onRowClick = (c: Column) => {
    if (c.isFixed) return;
    onToggleColumnVisibility(c.key);
  };

  const setGroupVisibility = (items: Column[], visible: boolean) => {
    items.forEach((c) => {
      if (c.isFixed) return;
      if (!!c.isVisible !== visible) onToggleColumnVisibility(c.key);
    });
  };

  const renderRow = (c: Column) => {
    const selected = !!c.isVisible;
    return (
      <Row
        key={c.key}
        selected={selected}
        fixed={c.isFixed}
        role="button"
        tabIndex={c.isFixed ? -1 : 0}
        onClick={() => onRowClick(c)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onRowClick(c);
          }
        }}
        id={id ? `${id}-available-${c.key}` : undefined}
        title={c.label}
      >
        <RowIcon
          iconName={
            c.isFixed ? "CheckMark" : selected ? "CheckMark" : "Add"
          }
          $selected={selected}
        />
        <RowLabel>{c.label}</RowLabel>
        {c.isFixed && <Locked>{t("greco.required", "Required")}</Locked>}
      </Row>
    );
  };

  return (
    <Wrapper>
      <Controls>
        <Dropdown
          label={t("greco.groupBy", "Group by")}
          selectedKey={grouping.key}
          options={groupByOptions}
          onChange={(_, o) => o && setGroupByKey(o.key as string)}
          styles={{ root: { minWidth: 180 } }}
          id={id ? `${id}-group-by` : undefined}
        />
        <SearchBoxWrap>
          <SearchBox
            placeholder={t("greco.search", "Search fields")}
            value={search}
            onChange={(_, v) => setSearch(v ?? "")}
            id={id ? `${id}-available-search` : undefined}
          />
        </SearchBoxWrap>
        <SelectAllWrap>
          <SelectAllCheckbox />
        </SelectAllWrap>
      </Controls>

      <ScrollArea>
        {buckets.length === 0 && (
          <EmptyState>{t("greco.noResults", "No fields match")}</EmptyState>
        )}

        {isFlat
          ? buckets[0].items.map(renderRow)
          : buckets.map((b) => {
              const selectedCount = b.items.filter((c) => c.isVisible).length;
              const allSelected = selectedCount === b.items.length;
              const open = isExpanded(b.key);
              return (
                <Group key={b.key}>
                  <GroupHeader
                    onClick={() => toggleExpanded(b.key)}
                    id={id ? `${id}-group-${b.key}` : undefined}
                  >
                    <Icon
                      iconName={open ? "ChevronDown" : "ChevronRight"}
                      styles={{ root: { fontSize: 10, width: 12 } }}
                    />
                    <GroupLabel>{b.label}</GroupLabel>
                    <GroupCount>
                      {selectedCount} / {b.items.length}
                    </GroupCount>
                    <AddAll
                      onClick={(e) => {
                        e.stopPropagation();
                        setGroupVisibility(b.items, !allSelected);
                      }}
                    >
                      {allSelected
                        ? t("greco.removeAll", "Remove all")
                        : t("greco.addAll", "Add all")}
                    </AddAll>
                  </GroupHeader>
                  {open && <GroupItems>{b.items.map(renderRow)}</GroupItems>}
                </Group>
              );
            })}
      </ScrollArea>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
`;

const Controls = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 0 4px 8px 4px;
`;

const SearchBoxWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const SelectAllWrap = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  padding-bottom: 6px;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
`;

const EmptyState = styled.div`
  padding: 16px 8px;
  color: ${(p) => p.theme.palette.neutralSecondary};
  font-style: italic;
`;

const Group = styled.div`
  margin-bottom: 4px;
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  cursor: pointer;
  user-select: none;
  background: ${(p) => p.theme.palette.neutralLighter};
  border-radius: 4px;
  &:hover {
    background: ${(p) => p.theme.palette.neutralLight};
  }
`;

const GroupLabel = styled.div`
  font-weight: 600;
  font-size: 13px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GroupCount = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.palette.neutralSecondary};
`;

const AddAll = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.palette.themePrimary};
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

const GroupItems = styled.div`
  padding: 2px 0 2px 14px;
`;

const Row = styled.div<{ selected?: boolean; fixed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 4px;
  cursor: ${(p) => (p.fixed ? "default" : "pointer")};
  opacity: ${(p) => (p.fixed ? 0.7 : 1)};
  background: ${(p) =>
    p.selected ? p.theme.palette.themeLighterAlt : "transparent"};
  &:hover {
    background: ${(p) =>
      p.fixed
        ? p.selected
          ? p.theme.palette.themeLighterAlt
          : "transparent"
        : p.theme.palette.neutralLighter};
  }
`;

const RowIcon = styled(Icon)<{ $selected?: boolean }>`
  font-size: 12px;
  width: 16px;
  color: ${(p) =>
    p.$selected ? p.theme.palette.themePrimary : p.theme.palette.neutralSecondary};
`;

const RowLabel = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
`;

const Locked = styled.div`
  font-size: 11px;
  color: ${(p) => p.theme.palette.neutralTertiary};
`;
